import { useQuery } from '@tanstack/react-query'
import api from '@/services/api'

/**
 * The market data the platform ACTUALLY has, for screens that used to print
 * constants under "LIVE FEED" labels.
 *
 * - Rates: GET /integrations/rates — the public NY Fed SOFR print and the US
 *   Treasury average interest rate. This is a real feed; Market Intel showed
 *   "SOFR 0%" and Hedge Desk a hardcoded 4.33% while it answered 3.87%.
 * - Provider status: GET /integrations/status — which data vendors (CoStar,
 *   REIS, Bloomberg, …) are connected. Market Intel said "4 feeds active"
 *   with none connected.
 */

export interface LiveRate {
  /** Index as the backend names it: SOFR_ON, UST_AVG. */
  indexName: string
  /** A FRACTION (0.0387), as the API returns it. */
  rate: number
  asOf: string | null
  provider: string
}

export interface ProviderStatus {
  provider: string
  displayName: string
  configured: boolean
  capabilities: string[]
  activeFor: string[]
}

/** What each public index is, in words a reader can check against its source. */
export const RATE_LABELS: Record<string, { label: string; source: string }> = {
  SOFR_ON: { label: 'SOFR (overnight)', source: 'NY Fed' },
  UST_AVG: { label: 'Treasury avg. interest rate', source: 'US Treasury, monthly' },
}

async function fetchRates(): Promise<LiveRate[]> {
  const res = await api.get<{ rates?: Array<Record<string, unknown>> }>('/integrations/rates')
  const rows = Array.isArray(res.data?.rates) ? res.data.rates : []
  return rows
    .filter((r) => typeof r.rate === 'number' && Number.isFinite(r.rate))
    .map((r) => ({
      indexName: String(r.index_name ?? ''),
      rate: r.rate as number,
      asOf: typeof r.as_of === 'string' ? r.as_of : null,
      provider: String(r.provider ?? ''),
    }))
}

async function fetchProviderStatus(): Promise<ProviderStatus[]> {
  const res = await api.get<Array<Record<string, unknown>>>('/integrations/status')
  const rows = Array.isArray(res.data) ? res.data : []
  return rows.map((r) => ({
    provider: String(r.provider ?? ''),
    displayName: String(r.display_name ?? r.provider ?? ''),
    configured: r.configured === true,
    capabilities: Array.isArray(r.capabilities) ? (r.capabilities as string[]) : [],
    activeFor: Array.isArray(r.active_for) ? (r.active_for as string[]) : [],
  }))
}

export function useLiveRates(enabled = true) {
  return useQuery({
    queryKey: ['integrations', 'rates'],
    queryFn: fetchRates,
    enabled,
    staleTime: 10 * 60_000,
    retry: 1,
  })
}

export function useProviderStatus(enabled = true) {
  return useQuery({
    queryKey: ['integrations', 'status'],
    queryFn: fetchProviderStatus,
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/** The live SOFR print as a fraction, or null when the feed has not answered. */
export function sofrOf(rates: LiveRate[] | undefined): LiveRate | null {
  return rates?.find((r) => r.indexName === 'SOFR_ON') ?? null
}
